import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../Layout/Layout";
import { isAuthenticated } from "../../auth";
import { listSellerProviderLinks, unlinkSellerProviderProduct, restoreSellerProviderLink } from "../services/providerProductService";
import DropshippingInterneLayout from "../components/DropshippingInterneLayout";
import LinkedProviderProductCard from "../components/LinkedProviderProductCard";
import ResyncModal from "./ResyncModal";
import { useDropshippingText } from "../i18n";
import { elakayAutoText } from "../../pageInside/dataElakay";
import "./DropshippingIn.css";
import "./SellerProviderProducts.css";

// "My Linked Provider Products" (ticket §3/§4/§5) -- a card grid, matching the rest of Elakay's
// card system (see LinkedProviderProductCard), backed ONLY by the real relations
// (SellerProviderProductLink + the Seller's own Product) returned by
// sellerProviderLinkService#listLinksForSeller. No fictional stats: Connected Providers/Linked/
// Active/Inactive are all derived from the same `links` array already fetched for the grid.
const SellerProviderProducts = ({ infoSource }) => {
  const ct = useDropshippingText(infoSource, "dropshippingCommercial");
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [unlinking, setUnlinking] = useState({});
  const [resyncMessages, setResyncMessages] = useState({});
  const [restoring, setRestoring] = useState({});
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all"); // all | active | inactive
  const [resyncTarget, setResyncTarget] = useState(null);

  const load = () => {
    const token = isAuthenticated()?.token;
    setLoading(true);
    listSellerProviderLinks(token).then((result) => {
      if (result?.error) {
        setError(result.error);
      } else {
        setLinks(result.items || []);
      }
      setLoading(false);
    });
  };

  useEffect(load, []);

  const handleUnlink = async (linkId) => {
    // Unlink = immediate, definitive removal of THIS Seller's product (no trash window).
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(ct("dsi_unlinkConfirm"))) return;
    setUnlinking((prev) => ({ ...prev, [linkId]: true }));
    setError("");
    const token = isAuthenticated()?.token;
    const result = await unlinkSellerProviderProduct(token, linkId);
    setUnlinking((prev) => ({ ...prev, [linkId]: false }));
    if (result?.error) {
      setError(result.code === "ACTIVE_ORDERS" ? ct("dsi_errActiveOrders") : result.error);
      return;
    }
    setNotice(ct("dsi_unlinkOk"));
    setLinks((prev) => prev.filter((link) => link._id !== linkId));
  };

  const handleRestore = async (linkId) => {
    setRestoring((prev) => ({ ...prev, [linkId]: true }));
    setResyncMessages((prev) => ({ ...prev, [linkId]: null }));
    const token = isAuthenticated()?.token;
    const result = await restoreSellerProviderLink(token, linkId);
    setRestoring((prev) => ({ ...prev, [linkId]: false }));
    if (result?.error) {
      setResyncMessages((prev) => ({ ...prev, [linkId]: { type: "error", text: elakayAutoText("h_lppc_restore_failed", "Unable to restore this product. Please try again.") } }));
      return;
    }
    setLinks((prev) => prev.map((link) => (
      link._id === linkId
        ? { ...link, ...result.link, providerProductId: { ...link.providerProductId, ...result.providerProduct }, sellerProductState: { isDeleted: false, canBeRestored: true } }
        : link
    )));
    setResyncMessages((prev) => ({ ...prev, [linkId]: { type: "success", text: elakayAutoText("h_lppc_restored", "Product restored to your shop.") } }));
  };

  // The Resynchronize modal owns its own network calls; it reports back just enough (updated link
  // fields, or nothing but a "something changed, refetch" signal) to patch THIS row in place --
  // never a full-page reload, and never re-fetching while the modal itself is still open.
  const handleSynced = (linkId, result) => {
    if (result?.link) {
      setLinks((prev) => prev.map((link) => (
        link._id === linkId ? { ...link, ...result.link, providerProductId: { ...link.providerProductId, ...result.providerProduct } } : link
      )));
    } else {
      // Fulfillment/pricing changes made inside Manual Synchronization update the Product and the
      // link independently (ticket §22 -- see ResyncModal's own comment) -- a lightweight
      // re-fetch of this one link is simpler and just as correct as reconciling every field by hand.
      const token = isAuthenticated()?.token;
      listSellerProviderLinks(token).then((result2) => { if (!result2?.error) setLinks(result2.items || []); });
    }
  };

  const stats = useMemo(() => {
    const providers = new Set(links.map((l) => String(l.providerId)));
    const activeCount = links.filter((l) => l.sellerProduct?.sellActive?.sell === true).length;
    return {
      providers: providers.size,
      linked: links.length,
      active: activeCount,
      inactive: Math.max(0, links.length - activeCount),
    };
  }, [links]);

  const visibleLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return links.filter((link) => {
      // A restorable (trashed/unlinked) product is its own state, not Active/Inactive -- always
      // shown regardless of the scope filter, but still respects a search.
      if (link.sellerProductState?.isDeleted) return !q || (link.providerProductId?.name || "").toLowerCase().includes(q);
      const active = link.sellerProduct?.sellActive?.sell === true;
      if (scope === "active" && !active) return false;
      if (scope === "inactive" && active) return false;
      if (!q) return true;
      const name = (link.sellerProduct?.name || link.providerProductId?.name || "").toLowerCase();
      return name.includes(q);
    });
  }, [links, search, scope]);

  return (
    <Layout
      infoSource={infoSource}
      titleElakay="sellerProviderProducts"
      SliderPunShow={false}
      loading={false}
      outOffPage={false}
      {...infoSource?.allSettings?.layout}
      showFooter={true}
    >
      <DropshippingInterneLayout infoSource={infoSource}>
        <div className="dsi-page seller-provider-products">
          <nav className="dsi-breadcrumb" aria-label={elakayAutoText("h_c766e665183df1", "Breadcrumb")}>
            <Link to="/myShop">{elakayAutoText("h_2af80dd16ddcb0", "Sell Center")}</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">{elakayAutoText("h_ea6ea55069ee28", "My Products")}</span>
          </nav>

          <div className="dsi-hero">
            <div className="dsi-hero__icon" aria-hidden="true">📦</div>
            <div className="dsi-hero__body">
              <h1>{elakayAutoText("h_lpp_title", "My Linked Provider Products")}</h1>
              <p className="dsi-hero__subtitle">{elakayAutoText("h_lpp_subtitle", "Manage the products you sell through Elakay Dropshipping In.")}</p>

              <label className="dsi-hero__search">
                <input
                  type="search"
                  placeholder={elakayAutoText("h_lpp_search", "Search linked products…")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label={elakayAutoText("h_lpp_search", "Search linked products…")}
                />
              </label>

              <div className="dsi-hero__stats" data-testid="linked-products-stats">
                <span data-testid="stat-providers"><strong>{stats.providers}</strong> {elakayAutoText("h_lpp_stat_providers", "Connected Providers")}</span>
                <span data-testid="stat-linked"><strong>{stats.linked}</strong> {elakayAutoText("h_lpp_stat_linked", "Linked Products")}</span>
                <span data-testid="stat-active"><strong>{stats.active}</strong> {elakayAutoText("h_lpp_stat_active", "Active Products")}</span>
                <span data-testid="stat-inactive"><strong>{stats.inactive}</strong> {elakayAutoText("h_lpp_stat_inactive", "Inactive Products")}</span>
              </div>
            </div>
          </div>

          <div className="dsi-toolbar">
            <div className="dsi-toolbar__scope" role="group" aria-label={elakayAutoText("h_lpp_filter", "Filter")}>
              {[["all", elakayAutoText("h_lpp_scope_all", "All")], ["active", elakayAutoText("h_lpp_scope_active", "Active")], ["inactive", elakayAutoText("h_lpp_scope_inactive", "Inactive")]].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`dsi-toolbar__scope-btn${scope === value ? " is-active" : ""}`}
                  aria-pressed={scope === value}
                  onClick={() => setScope(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <Link to="/seller/providers" className="seller-provider-products__link">
              {elakayAutoText("h_lpp_browse", "Browse Providers")} →
            </Link>
          </div>

          {loading && (
            <div className="dsi-skeleton" data-testid="dsi-skeleton" aria-hidden="true" aria-busy="true">
              <div className="dsi-skeleton__shop" />
            </div>
          )}
          {error && <p className="seller-provider-products__error">{error}</p>}
          {notice && <p className="seller-provider-products__message seller-provider-products__message--success" role="status">{notice}</p>}

          {!loading && !error && links.length === 0 && (
            <div className="dsi-state-card">
              <div className="dsi-state-card__icon" aria-hidden="true">📦</div>
              <h2>{elakayAutoText("h_lpp_empty_title", "No linked products yet")}</h2>
              <p>{elakayAutoText("h_lpp_empty_body", "You haven't linked any provider products yet.")}</p>
              <Link to="/seller/providers" className="dsi-state-card__btn">{elakayAutoText("h_lpp_browse", "Browse Providers")}</Link>
            </div>
          )}

          {!loading && !error && links.length > 0 && visibleLinks.length === 0 && (
            <p className="dsi-empty-filtered" role="status">{elakayAutoText("h_lpp_no_match", "No linked products match your search.")}</p>
          )}

          <div className="dsi-grid" data-testid="linked-products-grid">
            {visibleLinks.map((link) => (
              <LinkedProviderProductCard
                key={link._id}
                link={link}
                ct={ct}
                unlinking={Boolean(unlinking[link._id])}
                restoring={Boolean(restoring[link._id])}
                message={resyncMessages[link._id]}
                onUnlink={handleUnlink}
                onRestore={handleRestore}
                onResync={(target) => setResyncTarget(target)}
              />
            ))}
          </div>

          <ResyncModal
            show={Boolean(resyncTarget)}
            onHide={() => setResyncTarget(null)}
            link={resyncTarget}
            ct={ct}
            onSynced={handleSynced}
          />
        </div>
      </DropshippingInterneLayout>
    </Layout>
  );
};

export default SellerProviderProducts;
